<template>
  <PrimeDialog
    v-model:visible="showConfirmationDialog"
    :header="props.action.ConfirmationHeaderText"
    modal
    @close="onConfirmDialogClose"
    @cancel="onConfirmDialogClose"
    :loading="loading"
  >
    <div class="card flex flex-wrap justify-content-center gap-3">
      <div class="flex align-items-center">
        <PrimeCheckbox
          v-model="hasConfirmedDialog"
          inputId="confirmCheck1"
          name="confirm-action-check"
          :binary="true"
          :disabled="loading"
        />
        <label for="confirmCheck1" class="ml-2">
          {{ props.action.ConfirmationBodyText }}
        </label>
      </div>
    </div>
    <template #footer>
      <div class="flex gap-2 pt-1">
        <PrimeButton
          :label="props.action.Name"
          :icon="props.action.ButtonImageCss"
          :severity="props.severity"
          :disabled="!hasConfirmedDialog || disabled || loading"
          :loading="loading"
          @click="run"
          :title="actionTitle"
        />
        <PrimeButton
          label="Cancel"
          @click="onConfirmDialogClose"
          :loading="loading"
        />
      </div>
    </template>
  </PrimeDialog>

  <div v-if="show">
    <slot
      name="trigger"
      :execute="run"
      :loading="loading"
      :disabled="disabled"
      :action="props.action"
      :severity="props.severity"
      :computedButtonLabel="props.action.Name"
      :icon="props.action.ButtonImageCss"
    >
      <PrimeButton
        :label="props.action.Name"
        :icon="props.action.ButtonImageCss"
        :severity="props.severity"
        :disabled="disabled || loading"
        :loading="loading"
        @click="run"
        :title="actionTitle"
        :class="{
          [props.action.CssClass ?? '']: true,
        }"
      />
    </slot>

    <FormActionUserInput
      v-model:visible="userInputVisible"
      v-model:data="userInputValues"
      :actionParams="userInputActionParams!"
      :action="props.action"
      @submit="
        () => {
          closeUserInputDialogAndRun();
        }
      "
      @cancel="
        () => {
          userInputVisible = false;
          userInputValues = {};
        }
      "
      v-if="userInputActionParams && userInputActionParams.length > 0"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ActionParameterTypeEnum,
  ActionParameterValueTypeEnum,
  type IMetaAction,
  type IMetaActionParameter,
} from "../../types/scad";

const props = withDefaults(
  defineProps<{
    action: IMetaAction;
    severity?:
      | "success"
      | "info"
      | "secondary"
      | "contrast"
      | "warning"
      | "help"
      | "danger"
      | "";
    additionalData?: Indexable;
    beforeExecute?: (data: Indexable) => Promise<boolean>;
    clearUserInputOnClose?: boolean;
  }>(),
  {
    severity: "secondary",
    additionalData: () => ({}),
  },
);

const loading = defineModel<boolean>("loading");

const data = defineModel<Indexable>("data", {
  default: () => ({}),
});

const baseUrl = computed(() => {
  if (import.meta.client) {
    return window.location.origin;
  }
  return useRuntimeConfig().public.baseUrl;
});

const emit = defineEmits<{
  complete: [response: any];
}>();

const evaluator = new ExpressionEvaluator();
const {
  Url,
  Response,
  RequestData,
  ResponseData,
  execute,
  isError,
  hasConfirmationDialog: hasConfirmationDialogMeta,
} = useMetaAction(props.action, data.value);
const toast = useToast();
const showDialog = ref<boolean>(false);

const showConfirmationDialog = ref<boolean>(false);
const hasConfirmedDialog = ref<boolean>(false);

watch(
  () => data.value,
  (newValue) => {
    RequestData.value = newValue;
  },
);

const userInputActionParams = computed(() => {
  return props.action.ActionParameters?.filter(
    (p) =>
      p.ActionParameterType == ActionParameterTypeEnum.Request &&
      p.ActionParameterValueType == ActionParameterValueTypeEnum.UserInput,
  );
});

const userInputValues = ref<Indexable | null>({});
const userInputVisible = ref<boolean>(false);

function useUserInputDialog(actionParams: IMetaActionParameter[]) {
  if (props.clearUserInputOnClose) {
    userInputValues.value = {};
  }
  // Start each field from its parameter's Value (meta_action_parameter.value): a ticked box, a filled date.
  // Values already entered (when the dialog is not cleared on close) are kept.
  userInputValues.value = {
    ...userInputDefaults(actionParams),
    ...(userInputValues.value ?? {}),
  };
  userInputVisible.value = true;
}

async function closeUserInputDialogAndRun() {
  userInputVisible.value = false;

  if (userInputValues.value) {
    // Merge user input values into the context data
    const contextWithUserInput = {
      ...contextData.value,
      ...userInputValues.value,
    };

    await executeAction(contextWithUserInput);
  }
}

function onConfirmDialogClose() {
  // reset confirmation dialog state
  showConfirmationDialog.value = false;
  hasConfirmedDialog.value = false;
}

const isScadAdmin = false;

const actionTitle = computed(() => {
  let url = Url.value;
  return isScadAdmin ? url : "";
});

// Consolidated evaluation context to avoid duplication
const evaluationContext = computed(() => ({
  ...data.value,
  _extra: props.additionalData,
  _baseUrl: baseUrl.value,
}));

const contextData = computed(() => {
  return {
    ...data.value,
    _extra: props.additionalData,
    _baseUrl: baseUrl.value,
  };
});

const show = computed<boolean>(() => {
  if (props.action.ShowExpression) {
    return evaluator.evaluateExpression(
      props.action.ShowExpression,
      evaluationContext.value,
    );
  }
  return true;
});

const disabled = computed<boolean>(() => {
  if (props.action.DisabledExpression) {
    return evaluator.evaluateExpression(
      props.action.DisabledExpression,
      evaluationContext.value,
    );
  }
  return false;
});

async function run() {
  // Check if user input is required
  if (userInputActionParams.value && userInputActionParams.value.length > 0) {
    // Show user input dialog
    useUserInputDialog(userInputActionParams.value);
  } else {
    await executeAction(contextData.value);
  }
}

async function executeAction(executionContext: Indexable) {
  // if they need to confirm and have not done yet, show the popup
  if (hasConfirmationDialogMeta.value == true && !hasConfirmedDialog.value) {
    showConfirmationDialog.value = true;
    return;
  }

  // if there's a beforeExecute hook, call it
  if (props.beforeExecute) {
    const canProceed = await props.beforeExecute(executionContext);
    if (!canProceed) {
      return;
    }
  }

  loading.value = true;
  try {
    const res = await execute(executionContext);
    switch (res?.action?.toLowerCase()) {
      case "dialog":
        showDialog.value = true;
        break;
      case "error":
        toast.add({
          severity: "error",
          summary: "Error",
          detail: res.error?.message ?? String(res.error),
          life: 5000,
        });
      case "filedownload":
        const fileName = getFileNameFromHeaders(res.headers);
        downloadFile(res.data as Blob, fileName ?? "downloadedFile");
        break;
      case "navigate":
        if (res.url) {
          window.open(res.url, "_blank");
        }
        break;
    }
    emit("complete", {
      response: Response.value,
      ...res,
    });
  } catch (error) {
    toast.add({
      severity: "error",
      summary: "Unexpected Error",
      detail: error instanceof Error ? error.message : String(error),
      life: 5000,
    });
  } finally {
    loading.value = false;
    onConfirmDialogClose();
  }
}
</script>

<style scoped></style>
